// EditBusService.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  FiArrowLeft,
  FiSave,
  FiRefreshCw,
  FiTruck,
  FiUser,
  FiNavigation,
  FiHash,
  FiGlobe,
  FiMapPin,
  FiAlertCircle,
  FiTrash2,
  FiFileText,
  FiPlus,
  FiClock
} from 'react-icons/fi';
import { ClipLoader } from 'react-spinners';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BaseUrl from '../../Url/BaseUrl';

const EditBusService = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { busNo } = useParams();
  const { t } = useTranslation();

  const { arrivalAndDeparture } = useSelector(state => state.services);

  // Master data
  const [operators, setOperators] = useState([]);
  const [routePaths, setRoutePaths] = useState([]);
  const [states, setStates] = useState([]);
  const [zones, setZones] = useState([]);
  const [filteredZones, setFilteredZones] = useState([]);

  // Bus stands for selected route
  const [groupedStands, setGroupedStands] = useState([]);
  const [loadingRouteData, setLoadingRouteData] = useState(false);

  // Timetable entries: { id, standCode, time, sortId }
  const [timetableEntries, setTimetableEntries] = useState([]);
  const [originalTimetableEntries, setOriginalTimetableEntries] = useState([]);

  const [processingStands, setProcessingStands] = useState(new Set());

  // Pending new stand
  const [pendingNewStand, setPendingNewStand] = useState(null);
  const [showAddConfirmModal, setShowAddConfirmModal] = useState(false);
  const [pendingTime, setPendingTime] = useState('');
  const [pendingSortId, setPendingSortId] = useState('');

  // Contact numbers
  const [contactNumbers, setContactNumbers] = useState([{ dialCode: '91', number: '' }]);

  const [formData, setFormData] = useState({
    busNo: '',
    busRegistrationNumber: '',
    busName: '',
    description: '',
    operatorNumber: '',
    routePathNo: '',
    status: true,
    stateCode: '',
    zoneCode: '',
    busType: 'Ordinary'
  });

  const [originalFormData, setOriginalFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [originalContactNumbers, setOriginalContactNumbers] = useState([]);

  const previousRoutePathRef = useRef('');

  // ------------------------------------------------------------------
  // Phone number helpers
  // ------------------------------------------------------------------
  const parsePhoneNumber = (rawString) => {
    if (!rawString) return { dialCode: '91', numberPart: '' };

    let cleaned = rawString.trim().replace(/[\s\-\(\)]/g, '');
    let hasPlus = cleaned.startsWith('+');
    if (hasPlus) cleaned = cleaned.substring(1);

    let dialCode = '91';
    let numberPart = cleaned;

    if (/^\d{1,4}/.test(cleaned)) {
      if (cleaned.startsWith('91') && cleaned.length >= 12) {
        dialCode = '91';
        numberPart = cleaned.substring(2);
      } else if (cleaned.startsWith('1') && cleaned.length >= 11) {
        dialCode = '1';
        numberPart = cleaned.substring(1);
      } else if (cleaned.startsWith('44') && cleaned.length >= 12) {
        dialCode = '44';
        numberPart = cleaned.substring(2);
      } else if (cleaned.startsWith('61') && cleaned.length >= 11) {
        dialCode = '61';
        numberPart = cleaned.substring(2);
      } else if (cleaned.startsWith('971') && cleaned.length >= 13) {
        dialCode = '971';
        numberPart = cleaned.substring(3);
      } else {
        dialCode = '91';
        numberPart = cleaned;
      }
    }

    return { dialCode, numberPart };
  };

  const splitConcatenatedNumbers = (numberStr) => {
    const chunks = [];
    let remaining = numberStr;
    while (remaining.length >= 10) {
      chunks.push(remaining.substring(0, 10));
      remaining = remaining.substring(10);
    }
    if (remaining.length > 0) {
      chunks.push(remaining);
    }
    return chunks;
  };

  const normalizeContactNumbers = (rawArray) => {
    if (!rawArray || !Array.isArray(rawArray) || rawArray.length === 0) {
      return [{ dialCode: '91', number: '' }];
    }

    const result = [];
    const seen = new Set();

    for (let item of rawArray) {
      if (typeof item !== 'string') continue;

      let { dialCode, numberPart } = parsePhoneNumber(item);

      if (dialCode === '91' && numberPart.length > 15) {
        const chunks = splitConcatenatedNumbers(numberPart);
        for (let chunk of chunks) {
          if (chunk.length > 0) {
            const full = `${dialCode}${chunk}`;
            if (!seen.has(full)) {
              seen.add(full);
              result.push({ dialCode, number: chunk });
            }
          }
        }
      } else {
        let cleanNumber = numberPart.replace(/\D/g, '');
        if (cleanNumber.length > 15) cleanNumber = cleanNumber.substring(0, 15);
        if (cleanNumber.length > 0) {
          const full = `${dialCode}${cleanNumber}`;
          if (!seen.has(full)) {
            seen.add(full);
            result.push({ dialCode, number: cleanNumber });
          }
        }
      }
    }

    if (result.length === 0) {
      return [{ dialCode: '91', number: '' }];
    }
    return result;
  };

  // ------------------------------
  // LOAD MASTER DATA AND BUS DETAILS
  // ------------------------------
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [
          operatorsRes,
          routePathsRes,
          statesRes,
          zonesRes,
          busRes
        ] = await Promise.all([
          axios.get(`${BaseUrl.baseurl}BusOperators`, { headers: { accept: 'text/plain' } }),
          axios.get(`${BaseUrl.baseurl}RoutePaths`, { headers: { accept: 'text/plain' } }),
          axios.get(`${BaseUrl.baseurl}States`, { headers: { accept: 'text/plain' } }),
          axios.get(`${BaseUrl.baseurl}Zones`, { headers: { accept: 'text/plain' } }),
          axios.get(`${BaseUrl.baseurl}Buses/GetBusByBusNoOrRegNo${busNo}`, { headers: { accept: 'text/plain' } })
        ]);

        const responseData = busRes.data;
        const busData = responseData.bus || responseData;
        const busStandsData = responseData.busStands || [];

        // ✅ FIX: Filter active operators only (status === true), then sort A-Z by operatorName
        setOperators(
          (operatorsRes.data || [])
            .filter(op => op.status === true)
            .sort((a, b) =>
              (a.operatorName || '').localeCompare(b.operatorName || '', undefined, { sensitivity: 'base' })
            )
        );
        setRoutePaths(routePathsRes.data);
        setStates(statesRes.data);
        setZones(zonesRes.data);

        console.log('Bus Data:', busData);
        console.log('Bus Stands Data:', busStandsData);

        const initialFormData = {
          busNo: busData.busNo?.toString() || '',
          busRegistrationNumber: busData.busRegistrationNumber || '',
          busName: busData.busName || '',
          description: busData.description || '',
          operatorNumber: busData.operatorNumber || '',
          routePathNo: busData.routePathNo || '',
          status: busData.status ?? true,
          stateCode: busData.stateCode || '',
          zoneCode: busData.zoneCode || '',
          busType: busData.bType === 1 ? 'NonStop' : 'Ordinary'
        };

        setFormData(initialFormData);
        setOriginalFormData(initialFormData);
        previousRoutePathRef.current = busData.routePathNo || '';

        const parsedContacts = normalizeContactNumbers(busData.busContactNumber || []);
        setContactNumbers(parsedContacts);

        if (busData.stateCode) {
          const filtered = zonesRes.data.filter(zone => zone.stateCode === busData.stateCode);
          setFilteredZones(filtered);
        }

        // Build timetable entries — only arrivalTime and sortId
        let initialEntries = [];

        if (busStandsData && Array.isArray(busStandsData) && busStandsData.length > 0) {
          // Sort by sortId first, then by arrival time
          const sortedStands = [...busStandsData].sort((a, b) => {
            const sortA = a.sortId ?? 0;
            const sortB = b.sortId ?? 0;
            if (sortA !== sortB) return sortA - sortB;
            const timeA = a.arrivalTime || '';
            const timeB = b.arrivalTime || '';
            return timeA.localeCompare(timeB);
          });

          initialEntries = sortedStands.map(entry => {
            const arrival = entry.arrivalTime ? entry.arrivalTime.substring(0, 5) : '';
            return {
              id: entry.id || 0,
              standCode: entry.busStandCode,
              time: arrival,
              sortId: entry.sortId !== undefined && entry.sortId !== null ? String(entry.sortId) : ''
            };
          });
          console.log(`Loaded ${initialEntries.length} stops from API for bus ${busNo}`);
        } else {
          console.warn('No bus stands data found in API response');
        }

        setTimetableEntries(initialEntries);
        setOriginalTimetableEntries(JSON.parse(JSON.stringify(initialEntries)));

        if (initialEntries.length === 0) {
          toast.info(t('noStopsData', 'No stop timings data available for this bus. You can add stops manually.'));
        }

      } catch (error) {
        console.error('Error fetching data:', error);
        if (error.response?.status === 404) {
          toast.error(t('busNotFound', 'Bus service with number "{{busNo}}" not found.', { busNo }));
        } else {
          toast.error(t('failedToLoadBusData', 'Failed to load bus service data. Please try again later.'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [busNo, t]);

  // ------------------------------
  // FETCH BUS STANDS FOR SELECTED ROUTE
  // ------------------------------
  useEffect(() => {
    if (!formData.routePathNo) {
      setGroupedStands([]);
      return;
    }

    const fetchStands = async () => {
      setLoadingRouteData(true);
      try {
        const standsRes = await axios.get(
          `${BaseUrl.baseurl}BusStands/GetBusStandsByRoutePathId?rpi=${formData.routePathNo}`
        );
        let standsData = standsRes.data || [];
        standsData = standsData.filter(stand => stand.status === true);

        const villageMap = new Map();
        const orderedVillageCodes = [];

        standsData.forEach(stand => {
          const villageObj = stand.cityVillage || {};
          const villageCode = villageObj.cityVillageCode || stand.cityVillageCode || 'Unknown';
          let rawVillageName = villageObj.cityVillageName || stand.cityVillageName || '';
          let displayVillageName = rawVillageName && rawVillageName.trim() !== '' && rawVillageName !== villageCode
            ? rawVillageName.trim()
            : (villageCode === 'Unknown' ? 'Unknown Area' : villageCode);

          if (!villageMap.has(villageCode)) {
            villageMap.set(villageCode, {
              villageName: displayVillageName,
              stands: []
            });
            orderedVillageCodes.push(villageCode);
          }
          villageMap.get(villageCode).stands.push(stand);
        });

        const groupedArray = orderedVillageCodes.map(villageCode => {
          const group = villageMap.get(villageCode);
          const sortedStands = [...group.stands].sort((a, b) =>
            (a.busStandName || '').localeCompare(b.busStandName || '')
          );
          return {
            villageCode,
            villageName: group.villageName,
            stands: sortedStands
          };
        });

        setGroupedStands(groupedArray);
      } catch (error) {
        console.error('Failed to fetch bus stands:', error);
        toast.error(t('failedToLoadRouteData', 'Failed to load route details.'));
      } finally {
        setLoadingRouteData(false);
      }
    };

    fetchStands();
  }, [formData.routePathNo, t]);

  // ------------------------------
  // HANDLE ROUTE PATH CHANGE
  // ------------------------------
  const handleRoutePathChange = async (newRoutePathNo) => {
    if (newRoutePathNo === previousRoutePathRef.current) {
      return;
    }

    const confirmChange = window.confirm(
      t('confirmRouteChange',
        'Changing the route will remove ALL existing stop timings. You will need to set new timings for the stops on this route. Continue?'
      )
    );

    if (!confirmChange) {
      setFormData(prev => ({
        ...prev,
        routePathNo: previousRoutePathRef.current
      }));
      return;
    }

    try {
      setLoadingRouteData(true);

      const allExistingStops = originalTimetableEntries.filter(entry => entry.id > 0);

      if (allExistingStops.length > 0) {
        try {
          const deletePayload = allExistingStops.map(entry => ({
            id: entry.id,
            busStandCode: entry.standCode,
            busStand: null,
            busNo: parseInt(formData.busNo),
            bus: null,
            arrivalTime: entry.time ? `${entry.time}:00` : null,
            departureTime: null,
            status: true,
            sortId: entry.sortId ? parseInt(entry.sortId, 10) : 0
          }));

          await axios.delete(
            `${BaseUrl.baseurl}Buses/DeleteArrivalDepartureList`,
            {
              headers: {
                accept: '*/*',
                'Content-Type': 'application/json'
              },
              data: deletePayload
            }
          );
          toast.success(
            t('allStopsRemoved', 'All existing stop timings have been removed for the new route.')
          );
        } catch (batchError) {
          console.error('Batch delete failed, trying individual deletes:', batchError);
          let deletedCount = 0;
          for (const entry of allExistingStops) {
            try {
              await axios.delete(
                `${BaseUrl.baseurl}BusArrivalAndDepartureAtBusStands/${entry.id}`,
                { headers: { accept: 'text/plain' } }
              );
              deletedCount++;
            } catch (singleError) {
              console.error(`Failed to delete stop ${entry.standCode}:`, singleError);
            }
          }
          if (deletedCount > 0) {
            toast.success(
              t('stopsRemoved', '{{count}} stop(s) removed successfully.', { count: deletedCount })
            );
          }
        }
      }

      const standsRes = await axios.get(
        `${BaseUrl.baseurl}BusStands/GetBusStandsByRoutePathId?rpi=${newRoutePathNo}`
      );
      const newRouteStands = standsRes.data || [];

      const activeStands = newRouteStands.filter(stand => stand.status === true);

      const villageMap = new Map();
      const orderedVillageCodes = [];

      activeStands.forEach(stand => {
        const villageObj = stand.cityVillage || {};
        const villageCode = villageObj.cityVillageCode || stand.cityVillageCode || 'Unknown';
        let rawVillageName = villageObj.cityVillageName || stand.cityVillageName || '';
        let displayVillageName = rawVillageName && rawVillageName.trim() !== '' && rawVillageName !== villageCode
          ? rawVillageName.trim()
          : (villageCode === 'Unknown' ? 'Unknown Area' : villageCode);

        if (!villageMap.has(villageCode)) {
          villageMap.set(villageCode, {
            villageName: displayVillageName,
            stands: []
          });
          orderedVillageCodes.push(villageCode);
        }
        villageMap.get(villageCode).stands.push(stand);
      });

      const groupedArray = orderedVillageCodes.map(villageCode => {
        const group = villageMap.get(villageCode);
        const sortedStands = [...group.stands].sort((a, b) =>
          (a.busStandName || '').localeCompare(b.busStandName || '')
        );
        return {
          villageCode,
          villageName: group.villageName,
          stands: sortedStands
        };
      });

      setGroupedStands(groupedArray);

      setTimetableEntries([]);
      setOriginalTimetableEntries([]);

      previousRoutePathRef.current = newRoutePathNo;

      setFormData(prev => ({ ...prev, routePathNo: newRoutePathNo }));

      toast.info(
        t('selectStopsForNewRoute', 'Please select stops and set timings for the new route.')
      );

    } catch (error) {
      console.error('Error handling route change:', error);
      toast.error(t('failedToUpdateRoute', 'Failed to update route. Please try again.'));

      setFormData(prev => ({
        ...prev,
        routePathNo: previousRoutePathRef.current
      }));
    } finally {
      setLoadingRouteData(false);
    }
  };

  // ------------------------------
  // FILTER TIMETABLE ENTRIES NOT IN CURRENT ROUTE
  // ------------------------------
  useEffect(() => {
    if (groupedStands.length === 0) return;

    const validStandCodes = new Set();
    groupedStands.forEach(group => {
      group.stands.forEach(stand => validStandCodes.add(stand.busStandCode));
    });

    setTimetableEntries(prev => {
      const filtered = prev.filter(entry => validStandCodes.has(entry.standCode));
      return filtered;
    });

    setOriginalTimetableEntries(prev => {
      const filtered = prev.filter(entry => validStandCodes.has(entry.standCode));
      return filtered;
    });
  }, [groupedStands]);

  // ------------------------------
  // STAND TOGGLE
  // ------------------------------
  const handleStandToggle = async (standCode) => {
    const entry = timetableEntries.find(e => e.standCode === standCode);
    const isChecked = !!entry;

    if (isChecked) {
      const stand = groupedStands.flatMap(g => g.stands).find(s => s.busStandCode === standCode);
      const standName = stand?.busStandName || standCode;

      if (window.confirm(
        t('confirmRemoveStand', 'Are you sure you want to remove "{{standName}}" from the timetable? This will delete it permanently.', { standName })
      )) {
        await handleDeleteStand(entry);
      }
      return;
    }

    const stand = groupedStands.flatMap(g => g.stands).find(s => s.busStandCode === standCode);
    setPendingNewStand(standCode);
    setPendingTime('');
    setPendingSortId('');
    setShowAddConfirmModal(true);
  };

  const handlePendingTimeChange = (e) => {
    setPendingTime(e.target.value);
  };

  const handlePendingSortIdChange = (e) => {
    setPendingSortId(e.target.value.replace(/\D/g, ''));
  };

  const confirmAddStand = async () => {
    if (!pendingTime || pendingTime.trim() === '') {
      toast.error(t('pleaseSelectArrivalTime', 'Please select an arrival time.'));
      return;
    }
    if (pendingSortId === '' || pendingSortId === null) {
      toast.error(t('pleaseEnterSortNumber', 'Please enter a sort number.'));
      return;
    }
    if (!pendingNewStand) return;

    setShowAddConfirmModal(false);
    await handleAddStand(pendingNewStand, pendingTime, pendingSortId);
    setPendingNewStand(null);
    setPendingTime('');
    setPendingSortId('');
  };

  const cancelAddStand = () => {
    setShowAddConfirmModal(false);
    setPendingNewStand(null);
    setPendingTime('');
    setPendingSortId('');
  };

  const handleAddStand = async (standCode, time, sortId) => {
    if (processingStands.has(standCode)) return;

    setProcessingStands(prev => new Set(prev).add(standCode));

    try {
      const busNoInt = parseInt(formData.busNo);
      if (isNaN(busNoInt)) {
        toast.error(t('invalidBusNumber', 'Invalid bus number.'));
        setProcessingStands(prev => {
          const newSet = new Set(prev);
          newSet.delete(standCode);
          return newSet;
        });
        return;
      }

      const payload = [{
        busStandCode: standCode,
        busStand: null,
        busNo: busNoInt,
        bus: null,
        arrivalTime: time ? `${time}:00` : null,
        departureTime: null,
        status: true,
        sortId: sortId ? parseInt(sortId, 10) : 0
      }];

      const response = await axios.post(
        `${BaseUrl.baseurl}BusArrivalAndDepartureAtBusStands`,
        payload,
        {
          headers: {
            accept: 'text/plain',
            'Content-Type': 'application/json'
          }
        }
      );

      let newId = 0;
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        newId = response.data[0].id || 0;
      }

      setTimetableEntries(prev => [...prev, {
        id: newId,
        standCode,
        time: time || '',
        sortId: sortId || ''
      }]);
      setOriginalTimetableEntries(prev => [...prev, {
        id: newId,
        standCode,
        time: time || '',
        sortId: sortId || ''
      }]);

      const stand = groupedStands.flatMap(g => g.stands).find(s => s.busStandCode === standCode);
      const standName = stand?.busStandName || standCode;

      toast.success(t('standAdded', 'Stop "{{standName}}" added successfully!', { standName }));

    } catch (error) {
      console.error('Failed to add stand:', error);
      toast.error(t('failedToAddStand', 'Failed to add stop. Please try again.'));
      setTimetableEntries(prev => prev.filter(e => e.standCode !== standCode));
    } finally {
      setProcessingStands(prev => {
        const newSet = new Set(prev);
        newSet.delete(standCode);
        return newSet;
      });
    }
  };

  const handleDeleteStand = async (entry) => {
    if (!entry.id || entry.id === 0) {
      setTimetableEntries(prev => prev.filter(e => e.standCode !== entry.standCode));
      setOriginalTimetableEntries(prev => prev.filter(e => e.standCode !== entry.standCode));
      return;
    }

    if (processingStands.has(entry.standCode)) return;

    setProcessingStands(prev => new Set(prev).add(entry.standCode));

    try {
      await axios.delete(
        `${BaseUrl.baseurl}BusArrivalAndDepartureAtBusStands/${entry.id}`,
        { headers: { accept: 'text/plain' } }
      );

      setTimetableEntries(prev => prev.filter(e => e.standCode !== entry.standCode));
      setOriginalTimetableEntries(prev => prev.filter(e => e.standCode !== entry.standCode));

      toast.success(t('standDeleted', 'Stop removed successfully!'));

    } catch (error) {
      console.error('Failed to delete stand:', error);
      toast.error(t('failedToDeleteStand', 'Failed to remove stop. Please try again.'));
    } finally {
      setProcessingStands(prev => {
        const newSet = new Set(prev);
        newSet.delete(entry.standCode);
        return newSet;
      });
    }
  };

  // ------------------------------
  // TIME & SORT CHANGE — PUT update
  // ------------------------------
  const putStopUpdate = async (standCode, timeVal, sortVal) => {
    const entry = timetableEntries.find(e => e.standCode === standCode);
    if (!entry) return;

    const busNoInt = parseInt(formData.busNo);
    if (isNaN(busNoInt)) {
      toast.error(t('invalidBusNumber', 'Invalid bus number.'));
      return;
    }

    const payload = [{
      id: entry.id || 0,
      busStandCode: standCode,
      busStand: null,
      busNo: busNoInt,
      bus: null,
      arrivalTime: timeVal ? `${timeVal}:00` : null,
      departureTime: null,
      status: true,
      sortId: sortVal !== '' && sortVal !== null ? parseInt(sortVal, 10) : 0
    }];

    await axios.put(
      `${BaseUrl.baseurl}BusArrivalAndDepartureAtBusStands`,
      payload,
      {
        headers: {
          accept: 'text/plain',
          'Content-Type': 'application/json'
        }
      }
    );
  };

  const handleTimeChange = async (standCode, time) => {
    const entry = timetableEntries.find(e => e.standCode === standCode);
    if (!entry) return;
    if (processingStands.has(standCode)) return;

    setTimetableEntries(prev =>
      prev.map(e => (e.standCode === standCode ? { ...e, time: time || '' } : e))
    );

    setProcessingStands(prev => new Set(prev).add(standCode));

    try {
      await putStopUpdate(standCode, time, entry.sortId);

      setOriginalTimetableEntries(prev =>
        prev.map(e => (e.standCode === standCode ? { ...e, time: time || '' } : e))
      );
    } catch (error) {
      console.error('Failed to update arrival time:', error);
      toast.error(t('failedToUpdateTime', 'Failed to update time. Please try again.'));

      const originalEntry = originalTimetableEntries.find(e => e.standCode === standCode);
      if (originalEntry) {
        setTimetableEntries(prev =>
          prev.map(e => (e.standCode === standCode ? { ...e, time: originalEntry.time } : e))
        );
      }
    } finally {
      setProcessingStands(prev => {
        const newSet = new Set(prev);
        newSet.delete(standCode);
        return newSet;
      });
    }
  };

  const handleSortIdChange = async (standCode, sortId) => {
    const numeric = sortId.replace(/\D/g, '');
    const entry = timetableEntries.find(e => e.standCode === standCode);
    if (!entry) return;
    if (processingStands.has(standCode)) return;

    setTimetableEntries(prev =>
      prev.map(e => (e.standCode === standCode ? { ...e, sortId: numeric } : e))
    );

    setProcessingStands(prev => new Set(prev).add(standCode));

    try {
      await putStopUpdate(standCode, entry.time, numeric);

      setOriginalTimetableEntries(prev =>
        prev.map(e => (e.standCode === standCode ? { ...e, sortId: numeric } : e))
      );
    } catch (error) {
      console.error('Failed to update sort number:', error);
      toast.error(t('failedToUpdateSort', 'Failed to update sort number. Please try again.'));

      const originalEntry = originalTimetableEntries.find(e => e.standCode === standCode);
      if (originalEntry) {
        setTimetableEntries(prev =>
          prev.map(e => (e.standCode === standCode ? { ...e, sortId: originalEntry.sortId } : e))
        );
      }
    } finally {
      setProcessingStands(prev => {
        const newSet = new Set(prev);
        newSet.delete(standCode);
        return newSet;
      });
    }
  };

  // ------------------------------
  // BATCH DELETE UNCHECKED STOPS
  // ------------------------------
  const deleteUncheckedStops = async () => {
    const originalStandCodes = new Set(originalTimetableEntries.map(e => e.standCode));
    const currentStandCodes = new Set(timetableEntries.map(e => e.standCode));

    const removedStandCodes = [...originalStandCodes].filter(code => !currentStandCodes.has(code));

    if (removedStandCodes.length === 0) {
      return [];
    }

    const stopsToDelete = originalTimetableEntries.filter(
      entry => removedStandCodes.includes(entry.standCode) && entry.id > 0
    );

    if (stopsToDelete.length === 0) {
      return [];
    }

    try {
      const deletePayload = stopsToDelete.map(entry => ({
        id: entry.id,
        busStandCode: entry.standCode,
        busStand: null,
        busNo: parseInt(formData.busNo),
        bus: null,
        arrivalTime: entry.time ? `${entry.time}:00` : null,
        departureTime: null,
        status: true,
        sortId: entry.sortId ? parseInt(entry.sortId, 10) : 0
      }));

      await axios.delete(
        `${BaseUrl.baseurl}Buses/DeleteArrivalDepartureList`,
        {
          headers: {
            accept: '*/*',
            'Content-Type': 'application/json'
          },
          data: deletePayload
        }
      );

      return stopsToDelete;

    } catch (error) {
      console.error('Batch delete failed:', error);
      let deletedCount = 0;
      for (const entry of stopsToDelete) {
        try {
          await axios.delete(
            `${BaseUrl.baseurl}BusArrivalAndDepartureAtBusStands/${entry.id}`,
            { headers: { accept: 'text/plain' } }
          );
          deletedCount++;
        } catch (singleError) {
          console.error(`Failed to delete stop ${entry.standCode}:`, singleError);
        }
      }
      if (deletedCount > 0) {
        toast.success(t('stopsRemoved', '{{count}} stop(s) removed successfully.', { count: deletedCount }));
      }
      return stopsToDelete.slice(0, deletedCount);
    }
  };

  const handleContactNumberChange = (index, field, value) => {
    const updated = [...contactNumbers];
    if (field === 'dialCode') {
      updated[index] = { ...updated[index], dialCode: value };
    } else if (field === 'number') {
      const numericValue = value.replace(/\D/g, '').slice(0, 15);
      updated[index] = { ...updated[index], number: numericValue };
    }
    setContactNumbers(updated);
    if (formErrors.busContactNumber) setFormErrors(prev => ({ ...prev, busContactNumber: '' }));
  };

  const addContactNumber = () => {
    setContactNumbers([...contactNumbers, { dialCode: '91', number: '' }]);
  };

  const removeContactNumber = (index) => {
    if (contactNumbers.length > 1) {
      const updated = contactNumbers.filter((_, i) => i !== index);
      setContactNumbers(updated);
    } else {
      setContactNumbers([{ dialCode: '91', number: '' }]);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'busNumber') {
      return;
    } else if (name === 'stateCode') {
      setFormData(prev => ({ ...prev, [name]: value, zoneCode: '' }));
      // Also filter zones for the selected state
      const filtered = zones.filter(zone => zone.stateCode === value);
      setFilteredZones(filtered);
    } else if (name === 'busType') {
      setFormData(prev => ({ ...prev, [name]: value }));
    } else if (name === 'status') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'routePathNo') {
      handleRoutePathChange(value);
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }

    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.busRegistrationNumber.trim()) {
      errors.busRegistrationNumber = t('registrationNumberRequired');
    }

    if (!formData.operatorNumber) {
      errors.operatorNumber = t('selectOperatorRequired');
    }

    if (!formData.routePathNo) {
      errors.routePathId = t('selectRoutePathRequired');
    }

    const validNumbers = contactNumbers.filter(contact => contact.number && contact.number.trim() !== '');
    if (validNumbers.length > 0) {
      const invalid = validNumbers.some(contact => !/^\d{10,15}$/.test(contact.number.trim()));
      if (invalid) errors.busContactNumber = t('validContactNumberRequired');
    }

    if (!formData.stateCode.trim()) {
      errors.stateCode = t('stateRequired');
    }

    if (!formData.zoneCode.trim()) {
      errors.zoneCode = t('zoneRequired');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (contactNumbers.length > 0 && originalContactNumbers.length === 0) {
      const contacts = contactNumbers
        .filter(c => c.number && c.number.trim() !== '')
        .map(c => `+${c.dialCode}${c.number}`)
        .sort();
      setOriginalContactNumbers(contacts);
    }
  }, [contactNumbers]);

  // ------------------------------
  // SUBMIT
  // ------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(t('fixFormErrors'));
      return;
    }

    if (timetableEntries.length === 0) {
      toast.error(t('atLeastOneStand', 'Please select at least one bus stand.'));
      return;
    }

    const missingArrival = timetableEntries.some(
      e => !e.time || e.time.trim() === ''
    );
    if (missingArrival) {
      toast.error(t('enterArrivalTime', 'Please enter arrival time for every selected stop.'));
      return;
    }

    const missingSort = timetableEntries.some(
      e => e.sortId === '' || e.sortId === null || e.sortId === undefined
    );
    if (missingSort) {
      toast.error(t('enterSortNumber', 'Please enter sort number for every selected stop.'));
      return;
    }

    setIsSubmitting(true);

    try {
      const deletedStops = await deleteUncheckedStops();
      if (deletedStops.length > 0) {
        toast.success(
          t('stopsRemoved', '{{count}} stop(s) removed from timetable.', { count: deletedStops.length })
        );
      }

      const busTypeInt = formData.busType === 'NonStop' ? 1 : 0;

      const busObject = {
        busNo: parseInt(formData.busNo),
        busRegistrationNumber: formData.busRegistrationNumber.trim().toUpperCase(),
        busName: formData.busName.trim() || null,
        description: formData.description.trim() || null,
        operatorNumber: formData.operatorNumber,
        routePathNo: formData.routePathNo,
        status: formData.status,
        busContactNumber: contactNumbers
          .filter(contact => contact.number && contact.number.trim() !== '')
          .map(contact => `+${contact.dialCode}${contact.number}`),
        stateCode: formData.stateCode.trim().toUpperCase(),
        zoneCode: formData.zoneCode.trim().toUpperCase(),
        bType: busTypeInt
      };

      const busStandsPayload = timetableEntries.map(entry => {
        const original = originalTimetableEntries.find(orig => orig.standCode === entry.standCode);
        const id = entry.id || (original?.id || 0);
        return {
          id: id,
          busStandCode: entry.standCode,
          busStand: null,
          busNo: parseInt(formData.busNo),
          bus: null,
          arrivalTime: entry.time ? `${entry.time}:00` : null,
          departureTime: null,
          status: true,
          sortId: entry.sortId !== '' ? parseInt(entry.sortId, 10) : 0
        };
      });

      const finalPayload = {
        bus: busObject,
        busStands: busStandsPayload
      };

      console.log('Final Payload:', JSON.stringify(finalPayload, null, 2));

      const response = await axios.put(
        `${BaseUrl.baseurl}Buses`,
        finalPayload,
        {
          headers: {
            accept: 'text/plain',
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Update response:', response);

      setOriginalFormData({ ...formData });
      const updatedContacts = contactNumbers
        .filter(c => c.number && c.number.trim() !== '')
        .map(c => `+${c.dialCode}${c.number}`)
        .sort();
      setOriginalContactNumbers(updatedContacts);
      setOriginalTimetableEntries(JSON.parse(JSON.stringify(timetableEntries)));

      toast.success(t('busAndTimetableUpdated', 'Bus service updated successfully! '), {
        className: 'bg-white text-gray-900 shadow-lg',
        style: {
          background: '#ffffff',
          color: '#1f2937',
          border: '1px solid #e5e7eb'
        },
        autoClose: 5000
      });

      setTimeout(() => {
        navigate('/bus-finder/bus-services/manage', {
          state: { refresh: true }
        });
      }, 1500);

    } catch (error) {
      console.error('Update failed:', error);
      if (error.response) {
        if (error.response.status === 404) {
          toast.error(t('busServiceNotFound', 'Bus service not found. It may have been deleted.'));
        } else if (error.response.status === 400) {
          toast.error(t('invalidData', 'Invalid data. Please check your inputs.'));
        } else if (error.response.status === 405) {
          toast.error('Method not allowed – please check API endpoint (should be PUT /api/Buses)');
        } else if (error.response.status === 409) {
          toast.error(t('busNumberConflict', 'Bus number conflict. Please use a different number.'));
        } else {
          toast.error(t('serverError', 'Server error: {{status}} - {{statusText}}', {
            status: error.response.status,
            statusText: error.response.data || error.response.statusText
          }));
        }
      } else if (error.request) {
        toast.error(t('networkError', 'Network error. Please check your connection and try again.'));
      } else {
        toast.error(t('unexpectedError', 'An unexpected error occurred. Please try again.'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('confirmDeleteBusService', 'Are you sure you want to delete this bus service? This action cannot be undone.'))) {
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${BaseUrl.baseurl}Buses/${busNo}`, {
        headers: { accept: 'text/plain' }
      });

      toast.success(t('busServiceDeleted', 'Bus service deleted successfully! You can now go back using the Back button.'));

      setTimeout(() => {
        navigate('/bus-finder/bus-services/manage', {
          state: { refresh: true }
        });
      }, 1500);
    } catch (error) {
      console.error('Delete failed:', error);
      if (error.response?.status === 404) {
        toast.error(t('busServiceNotFoundDelete', 'Bus service not found. It may have already been deleted.'));
      } else {
        toast.error(t('failedToDeleteBusService', 'Failed to delete bus service. Please try again.'));
      }
    } finally {
      setDeleting(false);
      setDeleteConfirm(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm(t('confirmCancelChanges', 'Are you sure? All unsaved changes will be lost.'))) {
      navigate('/bus-finder/bus-services/manage', {
        state: { refresh: false }
      });
    }
  };

  const selectedOperator = operators.find(op => op.operatorNumber === formData.operatorNumber);
  const selectedRoute = routePaths.find(route => route.pathNo === formData.routePathNo);
  const selectedState = states.find(state => state.stateCode === formData.stateCode);
  const selectedZone = zones.find(zone => zone.zoneCode === formData.zoneCode);

  if (loading) {
    return (
      <div className="loading-container">
        <ClipLoader size={60} color="#00BCD4" />
        <p>{t('loadingBusServiceData', 'Loading bus service data...')}</p>
      </div>
    );
  }

  return (
    <div className="edit-bus-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="page-header">
        <button className="btn-icon" onClick={() => navigate(-1)} aria-label={t('back')}>
          <FiArrowLeft />
        </button>
        <h1>{t('editBusService', 'Edit Bus Service')}</h1>
      </div>

      <div className="registration-layout">
        <div className="form-column">
          <form onSubmit={handleSubmit} className="bus-registration-form">
            <section className="form-card">
              <h2 className="card-title">
                <FiTruck className="title-icon" />
                {t('busInformation')}
              </h2>
              <div className="form-grid">
                {/* Bus Service Number - STATIC */}
                <div className="form-group">
                  <label>
                    <FiHash /> {t('busServiceNumberLabel', 'Bus Service Number')} <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="busNumber"
                    value={formData.busNo}
                    disabled
                    className="form-input"
                  />
                  <small className="field-hint">{t('busNumberStaticHint', 'Bus service number is permanent and cannot be edited.')}</small>
                </div>

                {/* RTO Registration — NO maxLength now, users can freely edit existing values */}
                <div className="form-group">
                  <label>{t('rtoRegistrationLabel')} <span className="required">*</span></label>
                  <input
                    type="text"
                    name="busRegistrationNumber"
                    value={formData.busRegistrationNumber}
                    onChange={handleChange}
                    className={`form-input ${formErrors.busRegistrationNumber ? 'error' : ''}`}
                    placeholder="MP09FA1997"
                    disabled={isSubmitting || deleting}
                  />
                  {formErrors.busRegistrationNumber && <span className="field-error">{formErrors.busRegistrationNumber}</span>}
                  <small className="field-hint">{t('rtoFormatHint', 'Format: MP09FA1997')}</small>
                </div>

                {/* Bus Service Name */}
                <div className="form-group">
                  <label>{t('busServiceNameLabel', 'Bus Service Name')}</label>
                  <input
                    type="text"
                    name="busName"
                    value={formData.busName || ''}
                    onChange={handleChange}
                    className={`form-input ${formErrors.busName ? 'error' : ''}`}
                    placeholder="e.g., Volvo AC"
                    disabled={isSubmitting || deleting}
                  />
                  {formErrors.busName && <span className="field-error">{formErrors.busName}</span>}
                </div>

                {/* Bus Type */}
                <div className="form-group">
                  <label>{t('busType', 'Bus Type')} <span className="required">*</span></label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="busType"
                        value="Ordinary"
                        checked={formData.busType === 'Ordinary'}
                        onChange={handleChange}
                        disabled={isSubmitting || deleting}
                      />
                      <span>{t('ordinary', 'Ordinary')}</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="busType"
                        value="NonStop"
                        checked={formData.busType === 'NonStop'}
                        onChange={handleChange}
                        disabled={isSubmitting || deleting}
                      />
                      <span>{t('nonStop', 'Non‑Stop')}</span>
                    </label>
                  </div>
                  {formErrors.busType && <span className="field-error">{formErrors.busType}</span>}
                </div>

                {/* Description */}
                <div className="form-group full-width">
                  <label><FiFileText /> {t('description', 'Description')}</label>
                  <textarea
                    name="description"
                    value={formData.description || ''}
                    onChange={handleChange}
                    className="form-input"
                    rows="3"
                    placeholder={t('descriptionPlaceholder', 'Enter any additional details about the bus')}
                    disabled={isSubmitting || deleting}
                  />
                  <small className="field-hint">{t('descriptionHint', 'Optional: Provide extra information for passengers')}</small>
                </div>

                {/* Contact Numbers */}
                <div className="form-group full-width">
                  <label>{t('busContactLabel')}</label>
                  <div className="contact-numbers-container">
                    {contactNumbers.map((contact, index) => (
                      <div key={index} className="contact-number-row">
                        <div className="phone-input-wrapper">
                          <select
                            value={contact.dialCode}
                            onChange={(e) => handleContactNumberChange(index, 'dialCode', e.target.value)}
                            className="country-code-select"
                            disabled={isSubmitting || deleting}
                          >
                            <option value="91">🇮🇳 +91 (India)</option>
                            <option value="1">🇺🇸 +1 (USA/Canada)</option>
                            <option value="44">🇬🇧 +44 (UK)</option>
                            <option value="61">🇦🇺 +61 (Australia)</option>
                            <option value="971">🇦🇪 +971 (UAE)</option>
                            <option value="65">🇸🇬 +65 (Singapore)</option>
                            <option value="60">🇲🇾 +60 (Malaysia)</option>
                            <option value="64">🇳🇿 +64 (New Zealand)</option>
                            <option value="49">🇩🇪 +49 (Germany)</option>
                            <option value="33">🇫🇷 +33 (France)</option>
                            <option value="81">🇯🇵 +81 (Japan)</option>
                            <option value="86">🇨🇳 +86 (China)</option>
                            <option value="82">🇰🇷 +82 (South Korea)</option>
                            <option value="966">🇸🇦 +966 (Saudi Arabia)</option>
                            <option value="974">🇶🇦 +974 (Qatar)</option>
                            <option value="965">🇰🇼 +965 (Kuwait)</option>
                            <option value="968">🇴🇲 +968 (Oman)</option>
                            <option value="880">🇧🇩 +880 (Bangladesh)</option>
                            <option value="94">🇱🇰 +94 (Sri Lanka)</option>
                            <option value="977">🇳🇵 +977 (Nepal)</option>
                            <option value="92">🇵🇰 +92 (Pakistan)</option>
                            <option value="93">🇦🇫 +93 (Afghanistan)</option>
                            <option value="20">🇪🇬 +20 (Egypt)</option>
                            <option value="27">🇿🇦 +27 (South Africa)</option>
                            <option value="234">🇳🇬 +234 (Nigeria)</option>
                            <option value="254">🇰🇪 +254 (Kenya)</option>
                            <option value="55">🇧🇷 +55 (Brazil)</option>
                            <option value="52">🇲🇽 +52 (Mexico)</option>
                            <option value="34">🇪🇸 +34 (Spain)</option>
                            <option value="39">🇮🇹 +39 (Italy)</option>
                            <option value="7">🇷🇺 +7 (Russia)</option>
                            <option value="90">🇹🇷 +90 (Turkey)</option>
                          </select>
                          <input
                            type="tel"
                            value={contact.number}
                            onChange={(e) => handleContactNumberChange(index, 'number', e.target.value)}
                            className={`form-input ${formErrors.busContactNumber ? 'error' : ''}`}
                            placeholder={t('contactNumberPlaceholder', 'Enter mobile number')}
                            maxLength="15"
                            disabled={isSubmitting || deleting}
                          />
                        </div>
                        <button
                          type="button"
                          className="btn-icon-small"
                          onClick={() => removeContactNumber(index)}
                          aria-label={t('removeNumber')}
                          disabled={(contactNumbers.length === 1 && contact.number === '') || isSubmitting || deleting}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button type="button" className="btn-outline btn-add-number" onClick={addContactNumber} disabled={isSubmitting || deleting}>
                      <FiPlus /> {t('addNumber', 'Add Number')}
                    </button>
                  </div>
                  {formErrors.busContactNumber && <span className="field-error">{formErrors.busContactNumber}</span>}
                  <small className="field-hint">{t('contactMultipleHint', 'Select country code and enter phone number. You can add multiple.')}</small>
                </div>

                {/* State */}
                <div className="form-group">
                  <label><FiGlobe /> {t('stateLabel')} <span className="required">*</span></label>
                  <select
                    name="stateCode"
                    value={formData.stateCode}
                    onChange={handleChange}
                    className={`form-select ${formErrors.stateCode ? 'error' : ''}`}
                    disabled={isSubmitting || deleting}
                  >
                    <option value="">{t('selectStateOption')}</option>
                    {states.map(s => (
                      <option key={s.stateCode} value={s.stateCode}>{s.stateName} ({s.stateCode})</option>
                    ))}
                  </select>
                  {formErrors.stateCode && <span className="field-error">{formErrors.stateCode}</span>}
                </div>

                {/* Zone */}
                <div className="form-group">
                  <label><FiMapPin /> {t('zoneLabel')} <span className="required">*</span></label>
                  <select
                    name="zoneCode"
                    value={formData.zoneCode}
                    onChange={handleChange}
                    className={`form-select ${formErrors.zoneCode ? 'error' : ''}`}
                    disabled={!formData.stateCode || isSubmitting || deleting}
                  >
                    <option value="">{t('selectZoneOption')}</option>
                    {filteredZones.map(z => (
                      <option key={z.zoneCode} value={z.zoneCode}>{z.zoneName} ({z.zoneCode})</option>
                    ))}
                  </select>
                  {formErrors.zoneCode && <span className="field-error">{formErrors.zoneCode}</span>}
                  {!formData.stateCode && <small className="field-hint">{t('selectStateFirst')}</small>}
                </div>

                {/* Active/Inactive */}
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="status"
                      checked={formData.status}
                      onChange={handleChange}
                      className="form-checkbox"
                      disabled={isSubmitting || deleting}
                    />
                    <span>{t('activeService')}</span>
                  </label>
                  <small className="field-hint">{t('activeServiceHint')}</small>
                </div>
              </div>
            </section>

            {/* Operator Information */}
            <section className="form-card">
              <h2 className="card-title">
                <FiUser className="title-icon" />
                {t('operatorInformation')}
              </h2>
              <div className="form-group">
                <label>{t('selectOperatorLabel')} <span className="required">*</span></label>
                <select
                  name="operatorNumber"
                  value={formData.operatorNumber}
                  onChange={handleChange}
                  className={`form-select ${formErrors.operatorNumber ? 'error' : ''}`}
                  disabled={isSubmitting || deleting}
                >
                  <option value="">{t('selectOperatorOption')}</option>
                  {operators.map(op => (
                    <option key={op.operatorNumber} value={op.operatorNumber}>
                      {op.operatorName} ({op.operatorNumber})
                    </option>
                  ))}
                </select>
                {formErrors.operatorNumber && <span className="field-error">{formErrors.operatorNumber}</span>}
                {selectedOperator && (
                  <div className="info-box">
                    <p><strong>{t('contact')}:</strong> {Array.isArray(selectedOperator.busOperatorContactNumber)
                      ? selectedOperator.busOperatorContactNumber.join(', ')
                      : selectedOperator.busOperatorContactNumber}</p>
                    <p><strong>{t('androidAppLink')}:</strong> <a href={selectedOperator.appLink} target="_blank" rel="noopener noreferrer">{selectedOperator.appLink}</a></p>
                    <p><strong>{t('iosAppLink')}:</strong> <a href={selectedOperator.iOSAppLink} target="_blank" rel="noopener noreferrer">{selectedOperator.iOSAppLink}</a></p>
                    <p><strong>{t('website')}:</strong> <a href={selectedOperator.websiteContactLink} target="_blank" rel="noopener noreferrer">{selectedOperator.websiteContactLink}</a></p>
                  </div>
                )}
              </div>
            </section>

            {/* Route Information */}
            <section className="form-card">
              <h2 className="card-title">
                <FiNavigation className="title-icon" />
                {t('routeInformation')}
              </h2>
              <div className="form-group">
                <label>{t('selectRoutePathLabel')} <span className="required">*</span></label>
                <select
                  name="routePathNo"
                  value={formData.routePathNo}
                  onChange={handleChange}
                  className={`form-select ${formErrors.routePathId ? 'error' : ''}`}
                  disabled={isSubmitting || deleting || loadingRouteData}
                >
                  <option value="">{t('selectRouteOption')}</option>
                  {routePaths.map(r => (
                    <option key={r.routePathNo} value={r.routePathNo}>
                      {r.routePathName} ({r.routePathNo})
                    </option>
                  ))}
                </select>
                {formErrors.routePathId && <span className="field-error">{formErrors.routePathId}</span>}
                {selectedRoute && (
                  <div className="info-box">
                    <p><strong>{t('routeNumber')}:</strong> {selectedRoute.routeNo}</p>
                    <p><strong>{t('routeName')}:</strong> {selectedRoute.routePathName}</p>
                  </div>
                )}
                {loadingRouteData && (
                  <div className="loading-placeholder" style={{ marginTop: '0.5rem' }}>
                    <FiRefreshCw className="spin" /> {t('updatingStops', 'Updating stops...')}
                  </div>
                )}
              </div>
            </section>

            {/* Timetable Section */}
            {formData.routePathNo && (
              <section className="form-card timetable-card">
                <h2 className="card-title">
                  <FiClock className="title-icon" />
                  {t('stopsAndTimings', 'Stops & Timings')}
                </h2>
                {loadingRouteData ? (
                  <div className="loading-placeholder">
                    <FiRefreshCw className="spin" /> {t('loadingStops', 'Loading stops...')}
                  </div>
                ) : (
                  <>
                    {groupedStands.length === 0 ? (
                      <p className="empty-message">{t('noStandsFound', 'No active bus stands found for this route.')}</p>
                    ) : (
                      <div className="stands-container">
                        {groupedStands.map((group, groupIndex) => (
                          <div key={group.villageCode} className="village-group">
                            <div className="village-header">
                              <strong>{group.villageName}</strong> ({group.villageCode})
                            </div>
                            <div className="stands-grid">
                              {group.stands.map(stand => {
                                const entry = timetableEntries.find(e => e.standCode === stand.busStandCode);
                                const isChecked = !!entry;
                                const isProcessing = processingStands.has(stand.busStandCode);

                                return (
                                  <div key={stand.busStandCode} className="stand-item">
                                    <label className="stand-checkbox">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => handleStandToggle(stand.busStandCode)}
                                        disabled={isProcessing || isSubmitting || deleting}
                                      />
                                      <span className="stand-name">
                                        {stand.busStandName} ({stand.busStandCode})
                                      </span>
                                      {isProcessing && <span className="processing-indicator">...</span>}
                                    </label>
                                    {isChecked && (
                                      <div className="time-inputs-group">
                                        <div className="time-field">
                                          <label>{t('arrivalTime', 'Arrival')}</label>
                                          <input
                                            type="time"
                                            value={entry.time}
                                            onChange={(e) => handleTimeChange(stand.busStandCode, e.target.value)}
                                            className="time-picker"
                                            disabled={isProcessing || isSubmitting || deleting}
                                          />
                                        </div>
                                        <div className="time-field">
                                          <label>{t('sortNumber', 'Sort #')}</label>
                                          <input
                                            type="text"
                                            inputMode="numeric"
                                            value={entry.sortId}
                                            onChange={(e) => handleSortIdChange(stand.busStandCode, e.target.value)}
                                            className="time-picker sort-input"
                                            placeholder="1"
                                            maxLength="4"
                                            disabled={isProcessing || isSubmitting || deleting}
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            {groupIndex < groupedStands.length - 1 && <div className="village-separator"></div>}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="stops-summary" style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#64748b' }}>
                      {t('selectedStops', 'Selected stops: {{count}}', { count: timetableEntries.length })}
                      {originalTimetableEntries.length > 0 && (
                        <span style={{ marginLeft: '0.5rem' }}>
                          ({t('originallyHad', 'originally had {{count}}', { count: originalTimetableEntries.length })})
                        </span>
                      )}
                      <div style={{ marginTop: '0.4rem', fontStyle: 'italic' }}>
                        {t('stopsHint', 'Note: For each selected stop, provide arrival time and sort number. Sort number controls the order of stops.')}
                      </div>
                    </div>
                  </>
                )}
              </section>
            )}

            <div className="form-actions">
              <div className="form-actions-left">
                <button
                  type="button"
                  className="btn-danger"
                  onClick={() => setDeleteConfirm(true)}
                  disabled={isSubmitting || deleting}
                >
                  {deleting ? (
                    <><ClipLoader size={16} color="#fff" /> {t('deleting')}</>
                  ) : (
                    <><FiTrash2 /> {t('deleteBusService')}</>
                  )}
                </button>
              </div>
              <div className="form-actions-right">
                <button type="button" className="btn-secondary" onClick={handleCancel} disabled={isSubmitting || deleting}>
                  {t('cancel')}
                </button>
                <button type="submit" className="btn-primary" disabled={isSubmitting || deleting}>
                  {isSubmitting ? (
                    <><ClipLoader size={16} color="#fff" /> {t('updating')}</>
                  ) : (
                    <><FiSave /> {t('updateBusService')}</>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        <div className="preview-column">
          <div className="preview-card">
            <h2 className="preview-title">{t('updatePreview', 'Update Preview')}</h2>
            <div className="preview-content">
              <div className="preview-section">
                <h3>{t('busDetails')}</h3>
                <dl>
                  <dt>{t('busServiceNumber', 'Bus Service No')}:</dt><dd>{formData.busNo || '—'}</dd>
                  <dt>{t('rtoRegistration')}:</dt><dd>{formData.busRegistrationNumber || '—'}</dd>
                  <dt>{t('busServiceName', 'Bus Service Name')}:</dt><dd>{formData.busName || '—'}</dd>
                  <dt>{t('busType')}:</dt><dd>{formData.busType || '—'}</dd>
                  <dt>{t('description')}:</dt><dd>{formData.description || '—'}</dd>
                  <dt>{t('contactNumbers')}:</dt>
                  <dd>
                    {contactNumbers.filter(c => c.number && c.number.trim()).length > 0 ? (
                      <ul className="contact-preview-list">
                        {contactNumbers.filter(c => c.number && c.number.trim()).map((contact, idx) => (
                          <li key={idx}>+{contact.dialCode} {contact.number}</li>
                        ))}
                      </ul>
                    ) : '—'}
                  </dd>
                  <dt>{t('state')}:</dt><dd>{selectedState ? `${selectedState.stateName} (${selectedState.stateCode})` : '—'}</dd>
                  <dt>{t('zone')}:</dt><dd>{selectedZone ? `${selectedZone.zoneName} (${selectedZone.zoneCode})` : '—'}</dd>
                  <dt>{t('status')}:</dt>
                  <dd>
                    <span className={`status-badge ${formData.status ? 'status-active' : 'status-inactive'}`}>
                      {formData.status ? t('active') : t('inactive')}
                    </span>
                  </dd>
                </dl>
              </div>
              <div className="preview-section">
                <h3>{t('operator')}</h3>
                {selectedOperator ? (
                  <dl>
                    <dt>{t('name')}:</dt><dd>{selectedOperator.operatorName}</dd>
                    <dt>{t('number')}:</dt><dd>{selectedOperator.operatorNumber}</dd>
                  </dl>
                ) : <p className="text-muted">{t('noOperatorSelected')}</p>}
              </div>
              <div className="preview-section">
                <h3>{t('route')}</h3>
                {selectedRoute ? (
                  <dl>
                    <dt>{t('routeName')}:</dt><dd>{selectedRoute.routePathName}</dd>
                    <dt>{t('routeNumber')}:</dt><dd>{selectedRoute.routeNo}</dd>
                  </dl>
                ) : <p className="text-muted">{t('noRouteSelected')}</p>}
              </div>
              <div className="preview-section">
                <h3>{t('stops', 'Stops')}</h3>
                {timetableEntries.length === 0 ? (
                  <p className="text-muted">{t('noStopsSelected')}</p>
                ) : (
                  <ul className="stops-list">
                    {[...timetableEntries]
                      .sort((a, b) => (parseInt(a.sortId || 0, 10) - parseInt(b.sortId || 0, 10)))
                      .map(entry => {
                        // ✅ Find stand AND its village group
                        let stand = null;
                        let villageName = '';
                        for (const group of groupedStands) {
                          const found = group.stands.find(s => s.busStandCode === entry.standCode);
                          if (found) {
                            stand = found;
                            villageName = group.villageName;
                            break;
                          }
                        }
                        return (
                          <li key={entry.standCode} className="stop-item">
                            <div className="stop-left">
                              <span className="sort-badge">
                                #{entry.sortId !== '' && entry.sortId !== undefined ? entry.sortId : '—'}
                              </span>
                              <div className="stop-info">
                                <span className="stop-name">
                                  {stand?.busStandName || entry.standCode}
                                </span>
                                {villageName && (
                                  <span className="stop-village">
                                    <FiMapPin size={11} /> {villageName}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="stop-right">
                              {entry.time ? (
                                <span className="stop-time">{entry.time}</span>
                              ) : (
                                <span className="stop-time no-time">{t('noTime', 'No time')}</span>
                              )}
                            </div>
                          </li>
                        );
                      })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Stand Confirmation Modal */}
      {showAddConfirmModal && pendingNewStand && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <FiClock size={24} color="#2563eb" />
              <h3>{t('addStop', 'Add Stop')}</h3>
            </div>
            <div className="modal-content">
              {(() => {
                const stand = groupedStands.flatMap(g => g.stands).find(s => s.busStandCode === pendingNewStand);
                const standName = stand?.busStandName || pendingNewStand;
                return (
                  <>
                    <p>{t('selectTimeForStop', 'Select time for stop "{{standName}}"', { standName })}</p>
                    <div className="modal-time-input">
                      <label>{t('arrivalTime', 'Arrival Time')} <span className="required">*</span>:</label>
                      <input
                        type="time"
                        value={pendingTime}
                        onChange={handlePendingTimeChange}
                        className="form-input"
                        autoFocus
                      />
                    </div>
                    <div className="modal-time-input">
                      <label>{t('sortNumber', 'Sort Number')} <span className="required">*</span>:</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={pendingSortId}
                        onChange={handlePendingSortIdChange}
                        className="form-input"
                        placeholder="1"
                        maxLength="4"
                      />
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={cancelAddStand} disabled={processingStands.has(pendingNewStand)}>
                {t('cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={confirmAddStand}
                disabled={
                  !pendingTime ||
                  pendingSortId === '' ||
                  processingStands.has(pendingNewStand)
                }
              >
                {processingStands.has(pendingNewStand) ? (
                  <><ClipLoader size={16} color="#fff" /> {t('adding')}</>
                ) : (
                  t('add', 'Add')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <FiAlertCircle size={24} color="#dc3545" />
              <h3>{t('confirmDelete')}</h3>
            </div>
            <div className="modal-content">
              <p>{t('confirmDeleteMessage', 'Are you sure you want to delete the bus service "{{busName}}" (Bus No: {{busNo}})?', {
                busName: formData.busName || formData.busRegistrationNumber,
                busNo: formData.busNo
              })}</p>
              <p className="warning-text">{t('deleteWarning', 'This action cannot be undone. All associated data will be permanently deleted.')}</p>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setDeleteConfirm(false)} disabled={deleting}>
                {t('cancel')}
              </button>
              <button className="btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? t('deleting') : t('deletePermanently')}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .edit-bus-page {
          max-width: 1400px;
          margin: 0 auto;
          padding: 1rem;
          font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
          color: #1e293b;
          background: #f8fafc;
          min-height: 100vh;
        }
        .page-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }
        .page-header h1 { font-size: 1.5rem; font-weight: 600; color: #0f172a; margin: 0; }
        .btn-icon {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          color: #475569;
        }
        .btn-icon:hover { background: #f1f5f9; border-color: #cbd5e1; color: #0f172a; }
        .registration-layout {
          display: grid;
          grid-template-columns: 1fr 360px;
          gap: 1.5rem;
          align-items: start;
        }
        .form-column { min-width: 0; }
        .bus-registration-form { display: flex; flex-direction: column; gap: 1.5rem; }
        .form-card {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          padding: 1.5rem;
          border: 1px solid #eef2f6;
        }
        .card-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 1.2rem;
          font-weight: 600;
          margin: 0 0 1.25rem 0;
          color: #0f172a;
          border-bottom: 2px solid #e2e8f0;
          padding-bottom: 0.75rem;
        }
        .title-icon { color: #2563eb; }
        .form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.25rem; }
        .full-width { grid-column: span 2; }
        .form-group { display: flex; flex-direction: column; gap: 0.375rem; }
        .form-group label { font-weight: 500; font-size: 0.9rem; color: #334155; display: flex; align-items: center; gap: 0.25rem; }
        .required { color: #dc2626; margin-left: 2px; }
        .radio-group { display: flex; gap: 1.5rem; align-items: center; margin-top: 0.25rem; }
        .radio-label { display: flex; align-items: center; gap: 0.5rem; font-weight: normal; cursor: pointer; }
        .radio-label input[type="radio"] { width: 1rem; height: 1rem; accent-color: #2563eb; margin: 0; }
        .form-input, .form-select {
          padding: 0.6rem 0.75rem;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          font-size: 0.95rem;
          transition: border 0.2s, box-shadow 0.2s;
          background: white;
          width: 100%;
          box-sizing: border-box;
        }
        .form-input:focus, .form-select:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
        .form-input.error, .form-select.error { border-color: #dc2626; background-color: #fef2f2; }
        .form-input:disabled, .form-select:disabled { background: #e9ecef; cursor: not-allowed; opacity: 0.7; }
        textarea.form-input { resize: vertical; min-height: 80px; }
        .field-error { color: #dc2626; font-size: 0.8rem; }
        .field-hint { color: #64748b; font-size: 0.75rem; }
        .checkbox-group { justify-content: center; margin-top: 0.5rem; }
        .checkbox-group label { display: flex; align-items: center; gap: 0.5rem; cursor: pointer; font-weight: normal; }
        .form-checkbox { width: 1.1rem; height: 1.1rem; accent-color: #2563eb; margin: 0; }
        .info-box {
          background: #f8fafc;
          border-radius: 8px;
          padding: 0.75rem 1rem;
          margin-top: 0.75rem;
          border-left: 4px solid #2563eb;
          font-size: 0.9rem;
        }
        .info-box p { margin: 0.25rem 0; }
        .info-box a { color: #2563eb; text-decoration: none; word-break: break-all; }
        .info-box a:hover { text-decoration: underline; }
        .timetable-card { background: #f0f9ff; border-left: 4px solid #2563eb; }
        .stands-container { display: flex; flex-direction: column; gap: 1rem; }
        .village-group { margin-bottom: 0.5rem; }
        .village-header {
          font-size: 0.9rem;
          font-weight: 600;
          color: #1e40af;
          background: #e0f2fe;
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
          margin-bottom: 0.75rem;
        }
        .stands-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 0.75rem; }
        .stand-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: white;
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .stand-checkbox { display: flex; align-items: center; gap: 0.5rem; cursor: pointer; font-size: 0.95rem; flex: 1; min-width: 120px; }
        .stand-checkbox input[type="checkbox"] { width: 1rem; height: 1rem; accent-color: #2563eb; flex-shrink: 0; }
        .stand-checkbox input[type="checkbox"]:disabled { cursor: not-allowed; opacity: 0.6; }
        .processing-indicator {
          font-size: 0.8rem;
          color: #2563eb;
          animation: pulse 1s ease-in-out infinite;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        .time-inputs-group {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
          align-items: flex-end;
        }
        .time-field {
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
        }
        .time-field label {
          font-size: 0.7rem;
          color: #64748b;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        .time-picker { padding: 0.25rem 0.5rem; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 0.85rem; width: 110px; flex-shrink: 0; }
        .sort-input { width: 70px; text-align: center; }
        .time-picker:disabled { background: #e9ecef; cursor: not-allowed; opacity: 0.7; }
        .village-separator { margin: 1rem 0 0.5rem; border-top: 2px dashed #cbd5e1; }
        .loading-placeholder { display: flex; align-items: center; gap: 0.5rem; color: #475569; padding: 1rem; justify-content: center; }
        .empty-message { color: #64748b; text-align: center; padding: 1rem; }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .contact-numbers-container { display: flex; flex-direction: column; gap: 0.5rem; }
        .contact-number-row { display: flex; gap: 0.5rem; align-items: center; }
        .phone-input-wrapper { flex: 1; display: flex; gap: 0.5rem; }
        .country-code-select {
          width: 140px;
          padding: 0.6rem 0.5rem;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          font-size: 0.9rem;
          background: white;
          cursor: pointer;
        }
        .country-code-select:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
        .country-code-select:disabled { background: #e9ecef; cursor: not-allowed; }
        .btn-icon-small {
          background: none;
          border: none;
          color: #94a3b8;
          font-size: 1.2rem;
          cursor: pointer;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          line-height: 1;
        }
        .btn-icon-small:hover:not(:disabled) { color: #dc2626; background: #fee2e2; }
        .btn-icon-small:disabled { cursor: not-allowed; opacity: 0.5; }
        .btn-add-number { align-self: flex-start; margin-top: 0.25rem; }
        .btn-outline {
          padding: 0.5rem 1rem;
          background: white;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 500;
          color: #1e293b;
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          cursor: pointer;
          transition: all 0.2s;
        }
        .btn-outline:hover:not(:disabled) { background: #f1f5f9; border-color: #94a3b8; }
        .btn-outline:disabled { opacity: 0.5; cursor: not-allowed; }
        .status-message { display: flex; align-items: center; gap: 0.375rem; font-size: 0.85rem; padding: 0.25rem 0; }
        .status-message.success { color: #10b981; }
        .status-message.error { color: #dc2626; }
        .form-actions { display: flex; justify-content: space-between; align-items: center; margin-top: 0.5rem; }
        .form-actions-left, .form-actions-right { display: flex; gap: 1rem; }
        .btn-primary, .btn-secondary, .btn-danger {
          padding: 0.75rem 1.5rem;
          border-radius: 40px;
          font-weight: 600;
          font-size: 0.95rem;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
        }
        .btn-primary { background: #2563eb; color: white; box-shadow: 0 4px 8px rgba(37,99,235,0.2); }
        .btn-primary:hover:not(:disabled) { background: #1d4ed8; transform: translateY(-1px); box-shadow: 0 6px 12px rgba(37,99,235,0.25); }
        .btn-secondary { background: white; color: #334155; border: 1px solid #cbd5e1; }
        .btn-secondary:hover:not(:disabled) { background: #f1f5f9; border-color: #94a3b8; }
        .btn-danger { background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); color: white; }
        .btn-danger:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
        .btn-primary:disabled, .btn-secondary:disabled, .btn-danger:disabled { opacity: 0.5; cursor: not-allowed; }
        .preview-column { position: sticky; top: 1.5rem; }
        .preview-card {
          background: white;
          border-radius: 16px;
          box-shadow: 0 8px 20px rgba(0,0,0,0.06);
          border: 1px solid #eef2f6;
          overflow: hidden;
        }
        .preview-title {
          font-size: 1.2rem;
          font-weight: 600;
          padding: 1.25rem 1.5rem;
          margin: 0;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          color: #0f172a;
        }
        .preview-content { padding: 1.25rem 1.5rem 1.5rem; }
        .preview-section { margin-bottom: 1.5rem; }
        .preview-section h3 {
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #64748b;
          margin: 0 0 0.75rem 0;
          border-bottom: 1px dashed #e2e8f0;
          padding-bottom: 0.25rem;
        }
        .preview-section dl { display: grid; grid-template-columns: 1fr 2fr; gap: 0.25rem 0; font-size: 0.9rem; margin: 0; }
        .preview-section dt { color: #475569; font-weight: 500; }
        .preview-section dd { margin: 0; color: #0f172a; font-weight: 400; }
        .text-muted { color: #94a3b8; font-style: italic; }
        .status-badge { display: inline-block; padding: 0.25rem 0.75rem; border-radius: 20px; font-size: 0.85rem; font-weight: 500; }
        .status-active { background: #d4edda; color: #155724; }
        .status-inactive { background: #f8d7da; color: #721c24; }
        .contact-preview-list { list-style: none; padding: 0; margin: 0; }
        .contact-preview-list li { padding: 0.2rem 0; border-bottom: 1px dashed #e2e8f0; }

        /* ---------- NEW STOPS PREVIEW UI ---------- */
        .stops-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .stop-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 0.75rem;
          padding: 0.65rem 0.75rem;
          background: #f8fafc;
          border-radius: 10px;
          border: 1px solid #eef2f6;
          transition: all 0.2s ease;
        }
        .stop-item:hover {
          background: #f1f5f9;
          border-color: #cbd5e1;
        }
        .stop-left {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          min-width: 0;
          flex: 1;
        }
        .sort-badge {
          background: #2563eb;
          color: white;
          font-size: 0.72rem;
          font-weight: 700;
          padding: 0.2rem 0.5rem;
          border-radius: 6px;
          flex-shrink: 0;
          min-width: 30px;
          text-align: center;
          letter-spacing: 0.3px;
        }
        .stop-info {
          display: flex;
          flex-direction: column;
          gap: 0.1rem;
          min-width: 0;
          flex: 1;
        }
        .stop-name {
          font-weight: 600;
          color: #1e293b;
          font-size: 0.88rem;
          word-break: break-word;
          line-height: 1.3;
        }
        .stop-village {
          display: inline-flex;
          align-items: center;
          gap: 0.2rem;
          color: #64748b;
          font-size: 0.72rem;
          font-style: italic;
          font-weight: 400;
        }
        .stop-right {
          flex-shrink: 0;
        }
        .stop-time {
          display: inline-block;
          color: #1e40af;
          background: #dbeafe;
          font-weight: 600;
          font-size: 0.8rem;
          padding: 0.25rem 0.6rem;
          border-radius: 6px;
          white-space: nowrap;
        }
        .stop-time.no-time {
          color: #b91c1c;
          background: #fee2e2;
        }

        .modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        .modal {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          width: 90%;
          max-width: 500px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.2);
        }
        .modal-header { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1rem; }
        .modal-header h3 { margin: 0; color: #333; }
        .modal-content p { color: #666; margin-bottom: 1rem; }
        .modal-time-input { margin: 1rem 0; }
        .modal-time-input label { display: block; margin-bottom: 0.5rem; font-weight: 500; color: #334155; }
        .modal-time-input input { width: 100%; }
        .warning-text { color: #dc3545; font-weight: 500; font-size: 0.95rem; }
        .modal-actions { display: flex; justify-content: flex-end; gap: 1rem; margin-top: 1.5rem; }
        .loading-container { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 400px; }
        .loading-container p { margin-top: 1rem; color: #666; }
        .stops-summary { margin-top: 0.75rem; padding: 0.5rem 0.75rem; background: #f1f5f9; border-radius: 6px; font-size: 0.85rem; }

        @media (max-width: 1024px) { .registration-layout { grid-template-columns: 1fr 300px; gap: 1rem; } }
        @media (max-width: 900px) { .registration-layout { grid-template-columns: 1fr; } .preview-column { position: static; margin-top: 1rem; } .preview-card { max-width: 600px; margin: 0 auto; } }
        @media (max-width: 768px) {
          .form-grid { grid-template-columns: 1fr; }
          .full-width { grid-column: span 1; }
          .phone-input-wrapper { flex-direction: column; }
          .country-code-select { width: 100%; }
          .form-actions { flex-direction: column-reverse; gap: 1rem; }
          .form-actions-left, .form-actions-right { width: 100%; justify-content: stretch; }
          .btn-primary, .btn-secondary, .btn-danger { width: 100%; justify-content: center; }
          .modal { padding: 1.5rem; }
          .modal-actions { flex-direction: column; }
          .stands-grid { grid-template-columns: 1fr; }
          .stand-item { flex-wrap: wrap; }
          .time-inputs-group { width: 100%; }
          .time-field { flex: 1; }
          .time-picker { width: 100%; }
          .sort-input { width: 100%; }
          /* Responsive preview stops */
          .stop-item { padding: 0.6rem 0.6rem; gap: 0.5rem; }
          .stop-name { font-size: 0.83rem; }
          .sort-badge { font-size: 0.68rem; padding: 0.18rem 0.4rem; min-width: 26px; }
          .stop-time { font-size: 0.75rem; padding: 0.2rem 0.5rem; }
          .stop-village { font-size: 0.68rem; }
        }
        @media (max-width: 480px) {
          .page-header h1 { font-size: 1.2rem; }
          .btn-icon { width: 36px; height: 36px; }
          .form-card { padding: 1rem; }
          .card-title { font-size: 1.1rem; }
          .preview-title { padding: 1rem; font-size: 1.1rem; }
          .preview-content { padding: 1rem; }
          .preview-section dl { grid-template-columns: 1fr; gap: 0.1rem 0; }
        }
        @media (max-width: 400px) {
          .stop-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.4rem;
          }
          .stop-right { align-self: flex-end; }
        }
      `}</style>
    </div>
  );
};

export default EditBusService;